import React from 'react';
import { useTranslation } from 'react-i18next';
import { Field, useFormState } from 'react-final-form';
import { Container, Label, PRICE_TYPE, Title } from '../../styles';
import { CurrencyInput, LookUpPriceGroup, NumberInput, RadioGroup } from '@utriper/core-ui';
import { fetch, Method } from 'core/api/fetch';
import PoliciesFormFields from 'modules/Experience/components/PoliciesFormFields';
import { Grid2 } from '@mui/material';
import { Switch } from '@utriper/core-ui';

const RatesPoliciesForm = ({ form, experienceId }) => {
    const { t } = useTranslation();
    const translate = (key) => t(`experience_form.${key}`);
    const { values } = useFormState();
    const radioGroupOptions = [
        {
            id: PRICE_TYPE.FIXED,
            label: translate('price_type_options.fixed'),
        },
        {
            id: PRICE_TYPE.PRICE_GROUP,
            label: translate('price_type_options.price_group'),
        },
    ];
    const hasPriceGroup = values?.hasPriceGroup || false;

    const handleActive = (event) => {
        const checked = event?.target?.checked;

        const newValues = {
            ...values,
            allowOpenDateBooking: checked,
            price: null,
            priceGroupId: null,
            duration: null,
            currency: null,
        };
        form.initialize(newValues);
    };

    const getList = async ({ entity, search, input }) => {
        try {
            const url = `/api/v1.1.1/experiencer/search/${entity}?${search}=${input}&experienceId=${experienceId}`;

            const response = await fetch(url, {
                method: Method.GET,
                authenticated: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.data || !Array.isArray(response.data)) {
                return { data: [] };
            }

            return { data: response.data };
        } catch (error) {
            return { data: [] };
        }
    };

    return (
        <React.Fragment>
            <Title title={translate('rates_policies.title')} description={translate('rates_policies.description')} />

            <Grid2 container size={12} spacing={1}>
                <Grid2 container size={{ xs: 12, md: 6 }} direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                    {/* Allow Open Date Booking */}
                    <Container>
                        <Field type="checkbox" name="allowOpenDateBooking" component={Switch} label={translate('allow_open_date_booking')} input={{ checked: values?.allowOpenDateBooking ?? false, onChange: handleActive }} />
                    </Container>

                    {/* Price Type */}
                    {hasPriceGroup && (
                        <Container>
                            <Label>{translate('price_type')}</Label>
                            <Field name="priceType" component={RadioGroup} options={radioGroupOptions} row disabled={!values.allowOpenDateBooking} />
                        </Container>
                    )}

                    {/* Price and PriceGroup */}
                    <Container sx={{ '& .MuiAutocomplete-root': { width: '100%' } }}>
                        <Label>{values.priceType === PRICE_TYPE.FIXED ? translate('price_placeholder') : translate('price_group')}</Label>
                        {values.priceType === PRICE_TYPE.FIXED ? (
                            <Field
                                id="price"
                                name="price"
                                label={''}
                                currency={values.currency}
                                onCurrencyChange={(newCurrency) => {
                                    form.change('currency', newCurrency);
                                }}
                                disabled={!values.allowOpenDateBooking}
                                render={({ input, meta, ...rest }) => (
                                    <CurrencyInput
                                        {...rest}
                                        value={typeof input?.value === 'number' ? input?.value : 0}
                                        onChange={(newValue) => {
                                            input.onChange(newValue);
                                        }}
                                    />
                                )}
                            />
                        ) : (
                            <Field
                                id="priceGroupId"
                                name="priceGroupId"
                                render={({ input }) => (
                                    <LookUpPriceGroup
                                        {...input}
                                        label={translate('price_group_placeholder')}
                                        entityService={{ getList }}
                                        value={values?.priceGroupId}
                                        initialValue={values?.priceGroupId}
                                        onChange={(newValue) => {
                                            form.change('priceGroupId', newValue);
                                        }}
                                        disabled={!values.allowOpenDateBooking}
                                    />
                                )}
                            />
                        )}
                    </Container>

                    {/* Anticipation Days */}
                    <Container>
                        <Label>{translate('anticipation_days')}</Label>
                        <Field name="anticipationDays" component={NumberInput} label={t('coreUi.timeMayus.days')} placeholder={translate('anticipation_days')} />
                    </Container>

                    {/* Anticipation Hours */}
                    <Container>
                        <Label>{translate('anticipation_hours')}</Label>
                        <Field name="anticipationHours" component={NumberInput} label={t('coreUi.timeMayus.hours')} placeholder={translate('anticipation_hours')} />
                    </Container>

                    {/* Anticipation Minutes */}
                    <Container>
                        <Label>{translate('anticipation_minutes')}</Label>
                        <Field name="anticipationMinutes" component={NumberInput} label={t('coreUi.timeMayus.minutes')} placeholder={translate('anticipation_minutes')} />
                    </Container>
                </Grid2>

                <Grid2 container size={{ xs: 12, md: 6 }} direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                    <PoliciesFormFields form={form} hideAnticipation />
                </Grid2>
            </Grid2>
        </React.Fragment>
    );
};

export default RatesPoliciesForm;
